import React from "react";

import type { QuoteRejectionReason } from "./jupiterPayload";

export interface CtaFeedback {
    message: string;
    severity: "error" | "warning";
    /** Sits beside the message inside the CTA. Explains what the user is
     *  agreeing to when the label asks "Proceed?". */
    tooltip?: string;
    hoverMessage?: string;
    ctaClass?: string;
    inputClass?: string;
    durationMs?: number; // 0 = persist
}

interface UseSwapFeedbackParams {
    sendAmount: string;
    maxBalanceValue: number | null;
    sendTokenLabel: string | undefined;
    quoteError: Error | null;
    /** Set when Jupiter priced the swap but would not build it for this wallet. */
    quoteRejection: QuoteRejectionReason | null;
    sendTokenDecimals: number | undefined;
    priceImpact: number | undefined;
    mode: "buy" | "sell";
    receiveAmountDisplay: string;
    sendCurrencySymbol: string | undefined;
    receiveCurrencySymbol: string | undefined;
    solBalance: number | null;
}

interface UseSwapFeedbackReturn {
    ctaFeedback: CtaFeedback | null;
    inputError: "decimals" | "amountTooLarge" | null;
    /** Increments on each truthy `setInputError` call. Subscribe to fire a
     *  one-shot UI animation per cap rejection (e.g., AmountInput's shake). */
    rejectionCount: number;
    isErrorFeedback: boolean;
    setInputError: (error: "decimals" | "amountTooLarge" | null) => void;
    clearCtaFeedback: () => void;
}

const LOW_SOL_THRESHOLD = 0.0001;
const PRICE_IMPACT_WARNING_PCT = 2; // 2% threshold

function getQuoteRejectionMessage(reason: QuoteRejectionReason, sendTokenLabel: string | undefined): string {
    switch (reason) {
        case "insufficient_funds":
            return `Insufficient ${sendTokenLabel}`;
        case "insufficient_sol":
            return "Not Enough SOL for Fees";
        case "below_gasless_minimum":
            return "Add SOL or Increase Amount";
        case "missing_token_account":
            return "Token Account Required";
        case "unbuildable":
            return "Swap Unavailable, Try Again";
    }
}

export function useSwapFeedback(params: UseSwapFeedbackParams): UseSwapFeedbackReturn {
    const {
        sendAmount,
        maxBalanceValue,
        sendTokenLabel,
        quoteError,
        quoteRejection,
        sendTokenDecimals,
        priceImpact,
        mode,
        receiveAmountDisplay,
        sendCurrencySymbol,
        receiveCurrencySymbol,
        solBalance
    } = params;

    const [inputError, setInputErrorState] = React.useState<"decimals" | "amountTooLarge" | null>(null);
    /** Increments each time `setInputError` is called with a truthy value.
     *  Lets the input UI fire a one-shot rejection animation per cap-hit
     *  even when the error key string doesn't change between hits. */
    const [rejectionCount, setRejectionCount] = React.useState(0);
    const setInputError = React.useCallback((err: "decimals" | "amountTooLarge" | null): void => {
        setInputErrorState(err);
        if (err) setRejectionCount((c) => c + 1);
    }, []);
    const [ctaFeedback, setCtaFeedback] = React.useState<CtaFeedback | null>(null);
    const ctaFeedbackTimeoutRef = React.useRef<number | null>(null);

    const isErrorFeedback = !!ctaFeedback && ctaFeedback.severity === "error";

    const showCtaFeedback = React.useCallback(
        (feedback: CtaFeedback, errorKey?: "decimals" | "amountTooLarge") => {
            setCtaFeedback(feedback);

            if (errorKey) {
                setInputError(errorKey);
            }

            if (ctaFeedbackTimeoutRef.current) {
                window.clearTimeout(ctaFeedbackTimeoutRef.current);
                ctaFeedbackTimeoutRef.current = null;
            }

            if (feedback.durationMs && feedback.durationMs > 0) {
                ctaFeedbackTimeoutRef.current = window.setTimeout(() => {
                    setCtaFeedback(null);
                    setInputError(null);
                    ctaFeedbackTimeoutRef.current = null;
                }, feedback.durationMs);
            }
        },
        [setInputError]
    );

    const clearCtaFeedback = React.useCallback(() => {
        if (ctaFeedbackTimeoutRef.current) {
            window.clearTimeout(ctaFeedbackTimeoutRef.current);
            ctaFeedbackTimeoutRef.current = null;
        }
        setCtaFeedback(null);
        setInputError(null);
    }, [setInputError]);

    const getActiveFeedback = React.useCallback((): CtaFeedback | null => {
        const amountValue = parseFloat(sendAmount);
        const hasInput = sendAmount && Number.isFinite(amountValue) && amountValue > 0;

        // Insufficient Balance
        if (hasInput && maxBalanceValue !== null && amountValue > maxBalanceValue) {
            return {
                message: `Insufficient ${sendTokenLabel}`,
                severity: "error",
                inputClass:
                    "border-red-500 hover:border-red-500 focus-within:border-red-500 active:border-red-500 transition-colors duration-200",
                ctaClass: "bg-red-500 disabled:bg-red-500 disabled:text-white transition-colors duration-200",
                durationMs: 0
            };
        }

        // No Route
        if (quoteError && hasInput) {
            return {
                message: "Failed to Generate Quote",
                severity: "error",
                inputClass:
                    "border-red-500 hover:border-red-500 focus-within:border-red-500 active:border-red-500 transition-colors duration-200",
                ctaClass: "bg-red-500 disabled:bg-red-500 disabled:text-white transition-colors duration-200",
                durationMs: 0
            };
        }

        // Priced, but Jupiter would not build the transaction for this wallet
        if (quoteRejection && hasInput) {
            return {
                message: getQuoteRejectionMessage(quoteRejection, sendTokenLabel),
                severity: "error",
                inputClass:
                    "border-red-500 hover:border-red-500 focus-within:border-red-500 active:border-red-500 transition-colors duration-200",
                ctaClass: "bg-red-500 disabled:bg-red-500 disabled:text-white transition-colors duration-200",
                durationMs: 0
            };
        }

        // > Max Decimals
        if (inputError === "decimals") {
            return {
                message: `Too Many Decimals (Max ${sendTokenDecimals})`,
                severity: "error",
                inputClass:
                    "border-red-500 hover:border-red-500 focus-within:border-red-500 active:border-red-500 transition-colors duration-200",
                ctaClass: "bg-red-500 disabled:bg-red-500 disabled:text-white transition-colors duration-200",
                durationMs: 1000
            };
        }

        // > Max Number Supported
        if (inputError === "amountTooLarge") {
            return {
                message: "Amount Too Large",
                severity: "error",
                inputClass:
                    "border-red-500 hover:border-red-500 focus-within:border-red-500 active:border-red-500 transition-colors duration-200",
                ctaClass: "bg-red-500 disabled:bg-red-500 disabled:text-white transition-colors duration-200",
                durationMs: 1000
            };
        }

        // Use the same fee-exclusive impact as the footer. Net token amounts
        // also include account-creation costs and are not a market-impact measure.
        const priceImpactPercent = priceImpact ?? NaN;

        if (Number.isFinite(priceImpactPercent) && priceImpactPercent > PRICE_IMPACT_WARNING_PCT) {
            return {
                message: `${priceImpactPercent.toFixed(2)}% Price Impact, Proceed?`,
                severity: "warning",
                tooltip: "You may swap for a lower amount than you expect.",
                hoverMessage:
                    mode === "buy"
                        ? `Buy ${receiveAmountDisplay ? `${receiveAmountDisplay} ` : ""}${receiveCurrencySymbol || "USDv"} anyway`
                        : `Sell ${sendAmount ? `${sendAmount} ` : ""}${sendCurrencySymbol || "USDv"} anyway`,
                inputClass:
                    "border-yellow-500 hover:border-yellow-500 focus-within:border-yellow-500 active:border-yellow-500 transition-colors duration-200",
                ctaClass:
                    "bg-yellow-500 text-gray-900 hover:text-white active:text-white transition-colors duration-200",
                durationMs: 0
            };
        }

        // Low SOL Warning
        if (hasInput && solBalance !== null && solBalance < LOW_SOL_THRESHOLD) {
            return {
                message: `Low SOL (${solBalance.toFixed(4)}), Proceed?`,
                severity: "warning",
                tooltip:
                    "You may have insufficient SOL to complete this transaction, add more SOL to ensure the transaction goes through.",
                hoverMessage:
                    mode === "buy"
                        ? `Buy ${receiveAmountDisplay ? `${receiveAmountDisplay} ` : ""}${receiveCurrencySymbol || "USDv"} anyway`
                        : `Sell ${sendAmount ? `${sendAmount} ` : ""}${sendCurrencySymbol || "USDv"} anyway`,
                inputClass:
                    "border-yellow-500 hover:border-yellow-500 focus-within:border-yellow-500 active:border-yellow-500 transition-colors duration-200",
                ctaClass:
                    "bg-yellow-500 text-gray-900 hover:text-white active:text-white transition-colors duration-200",
                durationMs: 0
            };
        }

        return null;
    }, [
        sendAmount,
        maxBalanceValue,
        sendTokenLabel,
        quoteError,
        quoteRejection,
        inputError,
        sendTokenDecimals,
        priceImpact,
        mode,
        receiveAmountDisplay,
        sendCurrencySymbol,
        receiveCurrencySymbol,
        solBalance
    ]);

    // Sync computed feedback to state
    React.useEffect(() => {
        const feedback = getActiveFeedback();

        if (feedback) {
            if (ctaFeedback?.message !== feedback.message) {
                showCtaFeedback(feedback);
            }
        } else if (ctaFeedback) {
            clearCtaFeedback();
        }
    }, [
        sendAmount,
        inputError,
        maxBalanceValue,
        priceImpact,
        solBalance,
        quoteError,
        quoteRejection,
        ctaFeedback,
        getActiveFeedback,
        showCtaFeedback,
        clearCtaFeedback
    ]);

    return {
        ctaFeedback,
        inputError,
        rejectionCount,
        isErrorFeedback,
        setInputError,
        clearCtaFeedback
    };
}
